import React, { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { Building2, ChevronDown } from 'lucide-react';

const TenantSwitcher = () => {
  const { 
    isSuperAdmin, 
    availableTenants, 
    selectedTenant, 
    switchToTenant,
    loading
  } = useTenant();
  
  const [isOpen, setIsOpen] = useState(false);
  
  // Enhanced debugging
  useEffect(() => {
    console.log('🔍 TenantSwitcher Mount/Update Debug:', {
      isSuperAdmin,
      loading,
      availableTenants,
      availableTenantsType: typeof availableTenants,
      availableTenantsIsArray: Array.isArray(availableTenants),
      availableTenantsCount: availableTenants?.length,
      firstTenant: availableTenants?.[0],
      selectedTenant,
      activeTenantId: localStorage.getItem('activeTenantId')
    });
  }, [isSuperAdmin, availableTenants, selectedTenant, loading]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.tenant-switcher')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  // Show loading state while data is being fetched
  if (loading) {
    return (
      <div className="px-3 py-2 text-sm text-blue-600 bg-blue-100 rounded-lg border border-blue-300">
        ⏳ Loading tenant data...
      </div>
    );
  }
  
  // Don't render if not super admin
  if (!isSuperAdmin) {
    console.log('❌ TenantSwitcher: User is not super admin, not rendering');
    return null;
  }
  
  // Safety check to ensure availableTenants is an array
  if (!Array.isArray(availableTenants)) {
    console.error('❌ TenantSwitcher: availableTenants is not an array:', availableTenants);
    return (
      <div className="px-3 py-2 text-sm text-red-600 bg-red-100 rounded-lg border border-red-300">
        Error: Invalid tenants data (not an array)
      </div>
    );
  }
  
  // Check if no tenants available
  if (availableTenants.length === 0) {
    console.warn('⚠️ TenantSwitcher: No tenants in array');
    return (
      <div className="px-3 py-2 text-sm text-yellow-600 bg-yellow-100 rounded-lg border border-yellow-300">
        No tenants available (empty array)
      </div>
    );
  }
  
  console.log('✅ TenantSwitcher: Rendering dropdown with', availableTenants.length, 'tenants');
  
  const handleTenantSelect = async (tenantId) => {
    if (tenantId) {
      console.log('🔍 TenantSwitcher: User selected tenant:', tenantId);
      try {
        await switchToTenant(tenantId);
        console.log('✅ TenantSwitcher: Tenant switch completed');
      } catch (error) {
        console.error('❌ TenantSwitcher: Tenant switch failed:', error);
        // Error is already handled in TenantContext, just close dropdown
      }
    }
    setIsOpen(false);
  };
  
  const getCurrentTenantName = () => {
    if (!selectedTenant) return 'Select Tenant';
    const tenant = availableTenants.find(t => t.id === selectedTenant);
    console.log('🔍 TenantSwitcher: Current tenant lookup:', { selectedTenant, found: tenant });
    return tenant ? tenant.name : 'Select Tenant';
  };
  
  return (
    <div className="relative tenant-switcher">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
      >
        <Building2 className="w-4 h-4 text-gray-500" />
        <span className="hidden sm:block">{getCurrentTenantName()}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg py-1 z-20 border border-gray-200">
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Select Tenant</p>
            <p className="text-xs text-gray-500">View data from different organizations</p>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            {availableTenants.map((tenant) => {
              console.log('🔍 Rendering tenant option:', tenant);
              return (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantSelect(tenant.id)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between transition-colors duration-150 ${
                    selectedTenant === tenant.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                      {tenant.name ? tenant.name.charAt(0).toUpperCase() : '?'}
                    </div>
                    <div>
                      <p className="font-medium">{tenant.name || 'Unnamed Tenant'}</p>
                      <p className="text-xs text-gray-500">{tenant.slug || 'No slug'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {tenant.status && (
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        tenant.status === 'active' ? 'bg-green-100 text-green-800' :
                        tenant.status === 'trial' ? 'bg-blue-100 text-blue-800' :
                        tenant.status === 'suspended' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {tenant.status}
                      </span>
                    )}
                    
                    {selectedTenant === tenant.id && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          
          {selectedTenant && (
            <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-600">
                Currently viewing: <span className="font-medium">{getCurrentTenantName()}</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantSwitcher;